import { cn } from "cn";
import { Loader2, Mic } from "lucide-react";
import type * as React from "react";

// The v3.2 record mark and the buttons that carry it (style.css .dock idle,
// the pre-chart "Start visit"). The mark is the brand blue: it invites the
// user to start. Red stays for what is live or destructive (the recording
// dot, Stop, Delete), see RecorderBar and ConfirmSheet.

const MARK_SIZE = {
  compact: "size-6",
  default: "size-7",
  large: "size-9",
  meter: "size-10",
  dock: "size-11",
} as const;

const ICON_SIZE = {
  compact: "size-3.5",
  default: "size-3.5",
  large: "size-[18px]",
  meter: "size-[18px]",
  dock: "size-5",
} as const;

const STROKE = {
  compact: 2.5,
  default: 2.5,
  large: 2.5,
  meter: 2,
  dock: 2.25,
} as const;

const MARK_TONE = {
  // Start recording: solid brand with a white mic in both themes, like the
  // logo (on-brand turns near-black in dark mode).
  solid: "bg-v3-brand text-white",
  // The microphone is live (onboarding mic check): a brand tint.
  soft: "bg-v3-brand-tint text-v3-brand",
  // Waiting or blocked.
  muted: "bg-v3-surface-2 text-v3-text-3",
} as const;

export type RecordMarkSize = keyof typeof MARK_SIZE;

/** The round microphone mark. Decorative: label the button around it. */
export function RecordMark({
  size = "default",
  tone = "solid",
  busy = false,
  icon,
  className,
}: {
  size?: RecordMarkSize;
  tone?: keyof typeof MARK_TONE;
  /** A spinner in place of the microphone (starting up). */
  busy?: boolean;
  /** Another icon in place of the microphone (e.g. MicOff when blocked). */
  icon?: React.ReactNode;
  className?: string;
}): React.JSX.Element {
  return (
    <span
      aria-hidden
      className={cn(
        "flex shrink-0 items-center justify-center rounded-full",
        MARK_SIZE[size],
        MARK_TONE[tone],
        className,
      )}
      data-slot="record-mark"
      data-tone={tone}
    >
      {busy ? (
        <Loader2 className={cn(ICON_SIZE[size], "animate-spin")} />
      ) : (
        (icon ?? <Mic className={ICON_SIZE[size]} strokeWidth={STROKE[size]} />)
      )}
    </span>
  );
}

// The dock's width (style.css .dock: --dw min(540px, 100% - 32px)): full
// width on phones and narrow panels, never wider than 540px on desktop, and
// centred in whatever column holds it.
export const DOCK_MAX_WIDTH = "max-w-[540px]";

/**
 * The "Start recording" pill (New visit, Add recording, Dictate): an ink pill
 * that stands out from the page in both themes, with the record mark and a
 * band of light sweeping across the label like "slide to unlock"
 * (.v3-sweep-text in @rosc/ui/v32.css; static with reduced motion). It is
 * still a tap. Busy (starting, writing) keeps the full ink with a spinner
 * and its label, so it reads as working, not as unavailable; only a plain
 * disabled dock is dimmed.
 */
export function RecordDock({
  label,
  busy = false,
  className,
  ...props
}: Omit<React.ComponentPropsWithRef<"button">, "children"> & {
  label: React.ReactNode;
  busy?: boolean;
}): React.JSX.Element {
  return (
    <button
      aria-busy={busy || undefined}
      className={cn(
        "group mx-auto flex h-[68px] w-full items-center gap-3 rounded-full bg-v3-ink px-3 text-left text-v3-on-ink shadow-v3-float transition-[opacity,transform] duration-150 hover:opacity-95 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-v3-brand active:scale-[0.99] disabled:pointer-events-none",
        DOCK_MAX_WIDTH,
        busy ? "cursor-progress" : "disabled:opacity-50",
        className,
      )}
      data-busy={busy || undefined}
      data-slot="record-dock"
      type="button"
      {...props}
    >
      <RecordMark busy={busy} size="dock" />
      <span className="v3-sweep-text text-[17px] font-semibold">{label}</span>
    </button>
  );
}

/** An ink pill with the record mark ("Start visit" on a pre-chart draft). */
export function RecordButton({
  label,
  size = "default",
  className,
  ...props
}: Omit<React.ComponentPropsWithRef<"button">, "children"> & {
  label: React.ReactNode;
  size?: "compact" | "default" | "large";
}): React.JSX.Element {
  return (
    <button
      className={cn(
        "inline-flex shrink-0 items-center justify-center gap-2 rounded-full bg-v3-ink font-v3 font-semibold text-v3-on-ink shadow-[0_1px_2px_rgba(0,0,0,0.12)] transition-[opacity,transform] duration-150 hover:opacity-90 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-v3-brand active:scale-[0.97] disabled:pointer-events-none disabled:opacity-40",
        size === "compact" && "h-9 pl-1.5 pr-3.5 text-[13.5px]",
        size === "default" && "h-11 pl-2 pr-4 text-[14px] lg:h-10",
        size === "large" && "h-14 pl-2.5 pr-6 text-[16px]",
        className,
      )}
      data-slot="record-button"
      type="button"
      {...props}
    >
      <RecordMark size={size} />
      {label}
    </button>
  );
}
